import { useState } from 'react'
import { Box, Stack, SimpleGrid, Text, Group, Badge, Table, Anchor, Skeleton, ActionIcon, Tooltip } from '@mantine/core'
import { IconBug, IconAlertOctagon, IconRefresh } from '@tabler/icons-react'
import { PieChart, Pie, Cell, ResponsiveContainer } from 'recharts'
import { notifications } from '@mantine/notifications'
import { useQueryClient } from '@tanstack/react-query'
import { useJiraSummary, useJiraConfig } from '@/hooks/useJiraData'
import { WidgetCard, CardHeader } from '@/components/ui'
import { formatTimeAgo } from '@/utils/formatTimeAgo'
import { jiraApi } from '@/api/jira'
import { useAuthStore } from '@/store/auth'

const STATUS_COLORS = {
  todo:        '#49b6d6',
  in_progress: '#f59c1a',
  done:        '#2f9e44',
}

const STATUS_LABELS = {
  todo:        'A Fazer',
  in_progress: 'Em Progresso',
  done:        'Concluído',
}

export function JiraSummaryWidget() {
  const { data: summary, isLoading, isError } = useJiraSummary()
  const { data: config } = useJiraConfig()
  const { selectedTenantId } = useAuthStore()
  const qc = useQueryClient()
  const [syncing, setSyncing] = useState(false)

  const handleSync = async () => {
    if (!selectedTenantId || syncing) return
    setSyncing(true)
    try {
      await jiraApi.triggerPoll(selectedTenantId)
      qc.invalidateQueries({ queryKey: ['jira-summary'] })
      notifications.show({ color: 'teal', title: 'Sincronizado', message: 'Dados do Jira atualizados.' })
    } catch {
      notifications.show({ color: 'red', title: 'Erro', message: 'Falha ao sincronizar com Jira.' })
    } finally {
      setSyncing(false)
    }
  }

  if (isError || (!isLoading && !summary)) return null

  const todo        = summary?.by_status_category.todo        ?? 0
  const in_progress = summary?.by_status_category.in_progress ?? 0
  const done        = summary?.by_status_category.done        ?? 0
  const total       = todo + in_progress + done

  const statusData = [
    { key: 'todo',        value: todo,        color: STATUS_COLORS.todo },
    { key: 'in_progress', value: in_progress, color: STATUS_COLORS.in_progress },
    { key: 'done',        value: done,        color: STATUS_COLORS.done },
  ]

  const pct = (n: number) =>
    total > 0 ? `${Math.round((n / total) * 100)}%` : '0%'

  return (
    <WidgetCard accent="info" p={0}>
      <CardHeader
        title={`Jira ${summary?.project_key ?? ''}`}
        extra={
          <Group gap={6}>
            <Badge variant="light" color="gray" size="sm" leftSection={<IconRefresh size={11} />}>
              Sync: {formatTimeAgo(summary?.last_sync_at ?? null)}
            </Badge>
            <Tooltip label="Forçar sincronização">
              <ActionIcon
                variant="subtle"
                color="teal"
                size="sm"
                loading={syncing}
                onClick={handleSync}
              >
                <IconRefresh size={14} />
              </ActionIcon>
            </Tooltip>
          </Group>
        }
      />

      <Box p="md">
        {isLoading ? (
          <Skeleton height={160} radius="md" />
        ) : (
          <Stack gap="sm">

            {/* ── Row 1: Donut + KPI cards ─────────────────────────────── */}
            <Group gap="md" align="flex-start" wrap="nowrap">

              {/* Donut chart with centered total */}
              <Box style={{ position: 'relative', width: 120, height: 120, flexShrink: 0 }}>
                <ResponsiveContainer width={120} height={120}>
                  <PieChart>
                    <Pie
                      data={statusData}
                      innerRadius={35}
                      outerRadius={50}
                      dataKey="value"
                      stroke="none"
                      startAngle={90}
                      endAngle={-270}
                    >
                      {statusData.map((entry, i) => (
                        <Cell key={i} fill={entry.color} />
                      ))}
                    </Pie>
                  </PieChart>
                </ResponsiveContainer>
                {/* Centered total overlay */}
                <Box
                  style={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    textAlign: 'center',
                    pointerEvents: 'none',
                  }}
                >
                  <Text fw={700} size="lg" lh={1}>
                    {total}
                  </Text>
                  <Text size="10px" c="dimmed" lh={1.2}>
                    total
                  </Text>
                </Box>
              </Box>

              {/* KPI cards */}
              <SimpleGrid cols={3} spacing="xs" style={{ flex: 1 }}>
                {(['todo', 'in_progress', 'done'] as const).map((cat) => {
                  const count = summary?.by_status_category[cat] ?? 0
                  return (
                    <Box
                      key={cat}
                      ta="center"
                      p="xs"
                      style={{
                        borderRadius: 'var(--mantine-radius-md)',
                        background: 'var(--ai-body-bg, rgba(0,0,0,0.04))',
                        borderTop: `3px solid ${STATUS_COLORS[cat]}`,
                      }}
                    >
                      <Text fw={700} style={{ fontSize: '1.5rem', lineHeight: 1 }}>
                        {count}
                      </Text>
                      <Text size="xs" c="dimmed" mt={2}>
                        {STATUS_LABELS[cat]}
                      </Text>
                      <Badge
                        size="xs"
                        variant="light"
                        color={cat === 'todo' ? 'cyan' : cat === 'in_progress' ? 'yellow' : 'green'}
                        mt={4}
                      >
                        {pct(count)}
                      </Badge>
                    </Box>
                  )
                })}
              </SimpleGrid>
            </Group>

            {/* ── Row 2: Bugs + Impedimentos ───────────────────────────── */}
            {((summary?.bugs_open ?? 0) > 0 || (summary?.impediments ?? 0) > 0) && (
              <Group gap="xs">
                {(summary?.bugs_open ?? 0) > 0 && (
                  <Badge
                    color="orange"
                    variant="light"
                    size="sm"
                    leftSection={<IconBug size={12} />}
                  >
                    {summary?.bugs_open} bugs abertos
                  </Badge>
                )}
                {(summary?.impediments ?? 0) > 0 && (
                  <Badge
                    color="red"
                    variant="light"
                    size="sm"
                    leftSection={<IconAlertOctagon size={12} />}
                  >
                    {summary?.impediments} impedimentos
                  </Badge>
                )}
              </Group>
            )}

            {/* ── Row 3: Últimas atualizações ──────────────────────────── */}
            {summary?.recently_updated && summary.recently_updated.length > 0 && (
              <Stack gap={4}>
                <Text size="xs" fw={600} c="dimmed" tt="uppercase" style={{ letterSpacing: '0.05em' }}>
                  Últimas atualizações
                </Text>
                <Table verticalSpacing="xs" horizontalSpacing="xs" fz="xs">
                  <Table.Tbody>
                    {summary.recently_updated.slice(0, 3).map((issue) => (
                      <Table.Tr key={issue.jira_issue_key}>
                        <Table.Td w={60}>
                          <Anchor
                            href={config ? `${config.jira_base_url}/browse/${issue.jira_issue_key}` : '#'}
                            target="_blank"
                            size="xs"
                            fw={600}
                            style={{ color: STATUS_COLORS.todo, whiteSpace: 'nowrap' }}
                          >
                            {issue.jira_issue_key}
                          </Anchor>
                        </Table.Td>
                        <Table.Td>
                          <Text size="xs" lineClamp={1}>{issue.summary}</Text>
                        </Table.Td>
                        <Table.Td w={80}>
                          <Badge
                            size="xs"
                            color={
                              issue.status_category === 'done'
                                ? 'green'
                                : issue.status_category === 'in_progress'
                                ? 'yellow'
                                : 'cyan'
                            }
                            variant="light"
                          >
                            {issue.status_name}
                          </Badge>
                        </Table.Td>
                        <Table.Td w={56} ta="right">
                          <Text size="xs" c="dimmed" style={{ whiteSpace: 'nowrap' }}>
                            {formatTimeAgo(issue.jira_updated_at)}
                          </Text>
                        </Table.Td>
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>
                <Group justify="flex-end">
                  <Anchor href="/integracoes/jira" size="xs" c="dimmed">
                    Ver todos →
                  </Anchor>
                </Group>
              </Stack>
            )}

          </Stack>
        )}
      </Box>
    </WidgetCard>
  )
}
